// Executive overview: top-line KPIs across revenue, users, ops at a glance.
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, ArrowRight } from "lucide-react";

type Totals = {
  revenueCents7d: number;
  revenueCents30d: number;
  newUsers7d: number;
  totalUsers: number;
  tryons7d: number;
  errors7d: number;
  spend7d: number;
};

const fmtUSD = (cents: number) =>
  (cents / 100).toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
const fmtUSDFloat = (n: number) =>
  n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 });

export default function AdminOverview() {
  const [t, setT] = useState<Totals | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      const now = new Date();
      const d7 = new Date(now.getTime() - 7 * 86400_000).toISOString();
      const d30 = new Date(now.getTime() - 30 * 86400_000).toISOString();

      const [paddle7, paddle30, raz7, play7, tryons7, usage7, profiles] = await Promise.all([
        supabase.from("paddle_orders").select("amount_cents").eq("status", "paid").gte("created_at", d7),
        supabase.from("paddle_orders").select("amount_cents").eq("status", "paid").gte("created_at", d30),
        supabase.from("razorpay_orders").select("amount_paise").eq("status", "paid").gte("created_at", d7),
        supabase.from("play_orders").select("amount_micros").eq("status", "paid").gte("created_at", d7),
        supabase.from("tryons").select("id,created_at", { count: "exact", head: true }).gte("created_at", d7),
        supabase
          .from("gemini_usage")
          .select("estimated_cost_usd,status,created_at")
          .gte("created_at", d7)
          .limit(10000),
        supabase.from("profiles").select("user_id,created_at", { count: "exact" }).gte("created_at", d7),
      ]);

      const paddle7c = (paddle7.data ?? []).reduce((s, r) => s + (r.amount_cents ?? 0), 0);
      const raz7c = Math.round(((raz7.data ?? []).reduce((s, r) => s + (r.amount_paise ?? 0), 0) / 100) * 100);
      const play7c = Math.round(((play7.data ?? []).reduce((s, r) => s + Number(r.amount_micros ?? 0), 0) / 10_000));
      const paddle30c = (paddle30.data ?? []).reduce((s, r) => s + (r.amount_cents ?? 0), 0);

      const usage = usage7.data ?? [];
      const spend7 = usage.reduce((s, r) => s + Number(r.estimated_cost_usd ?? 0), 0);
      const errors7 = usage.filter((r) => r.status !== "ok").length;

      const { count: totalUsers } = await supabase
        .from("profiles")
        .select("user_id", { count: "exact", head: true });

      if (!active) return;
      setT({
        revenueCents7d: paddle7c + raz7c + play7c,
        revenueCents30d: paddle30c,
        newUsers7d: profiles.count ?? 0,
        totalUsers: totalUsers ?? 0,
        tryons7d: tryons7.count ?? 0,
        errors7d: errors7,
        spend7d: spend7,
      });
      setLoading(false);
    })();
    return () => {
      active = false;
    };
  }, []);

  if (loading || !t) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  const margin7 = t.revenueCents7d / 100 - t.spend7d;

  const kpis = [
    { label: "Revenue (7d)", value: fmtUSD(t.revenueCents7d), to: "/admin/revenue" },
    { label: "Revenue (30d)", value: fmtUSD(t.revenueCents30d), to: "/admin/revenue" },
    { label: "Gross margin (7d)", value: fmtUSDFloat(margin7), to: "/admin/operations" },
    { label: "New users (7d)", value: t.newUsers7d.toLocaleString(), to: "/admin/users" },
    { label: "Total users", value: t.totalUsers.toLocaleString(), to: "/admin/users" },
    { label: "Try-ons (7d)", value: t.tryons7d.toLocaleString(), to: "/admin/engagement" },
    { label: "Errors (7d)", value: t.errors7d.toLocaleString(), to: "/admin/stability" },
    { label: "AI spend (7d)", value: fmtUSDFloat(t.spend7d), to: "/admin/operations" },
  ];

  return (
    <div className="mx-auto max-w-7xl space-y-6 p-4">
      <div>
        <h1 className="font-display text-2xl font-bold tracking-tight">Overview</h1>
        <p className="text-sm text-muted-foreground">Top-line health for the last 7 days.</p>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {kpis.map((k) => (
          <Link key={k.label} to={k.to} className="group">
            <Card className="transition hover:border-primary/40">
              <CardContent className="p-4">
                <div className="text-xs text-muted-foreground">{k.label}</div>
                <div className="mt-1 text-xl font-semibold tabular-nums">{k.value}</div>
                <div className="mt-2 flex items-center gap-1 text-[10px] uppercase tracking-wider text-primary opacity-0 transition group-hover:opacity-100">
                  Open <ArrowRight className="h-3 w-3" />
                </div>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        <QuickLink to="/admin/revenue" title="Revenue" desc="By gateway, pack, top customers, LTV." />
        <QuickLink to="/admin/users" title="Users" desc="Signups, paid split, churn." />
        <QuickLink to="/admin/funnel" title="Funnel" desc="Signup → first try-on → paid." />
        <QuickLink to="/admin/stability" title="Stability" desc="Errors, edge function failures." />
        <QuickLink to="/admin/refunds" title="Refunds" desc="Paddle / Play / Razorpay refunds." />
        <QuickLink to="/admin/mobile" title="Mobile (Play)" desc="Installs, crashes, ratings." />
      </div>
    </div>
  );
}

function QuickLink({ to, title, desc }: { to: string; title: string; desc: string }) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <p className="mb-3 text-xs text-muted-foreground">{desc}</p>
        <Button asChild size="sm" variant="outline">
          <Link to={to}>
            Open <ArrowRight className="ml-1 h-3 w-3" />
          </Link>
        </Button>
      </CardContent>
    </Card>
  );
}
